import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { Paginated, Vehicle } from '@logistech/ui-shared';

const positiveInt = z.string().trim().refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) > 0), {
  message: 'Nombre entier positif',
});

// Aligné sur le schema Zod du maintenance-service (routes/plans/index.ts + validateIntervals)
const schema = z
  .object({
    vehicleId: z.string().uuid('Véhicule requis'),
    name: z.string().trim().min(2, 'Nom requis (2 caractères min.)').max(120),
    intervalKm: positiveInt,
    intervalMonths: positiveInt,
  })
  .refine((d) => d.intervalKm !== '' || d.intervalMonths !== '', {
    message: 'Renseigner un intervalle en km, en mois, ou les deux',
    path: ['intervalMonths'],
  });

type FormData = z.infer<typeof schema>;

const DEFAULTS: FormData = { vehicleId: '', name: '', intervalKm: '', intervalMonths: '' };

interface Props { open: boolean; onOpenChange: (open: boolean) => void }

/** Plan préventif récurrent : km, durée, ou les deux (« le premier atteint »). */
export function PlanDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => { if (open) reset(DEFAULTS); }, [open, reset]);

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: async () => (await api.get<Paginated<Vehicle>>('/vehicles?pageSize=100')).data,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (d: FormData) => {
      const km = d.intervalKm ? Number(d.intervalKm) : undefined;
      const days = d.intervalMonths ? Number(d.intervalMonths) * 30 : undefined;
      return api.post('/maintenance/plans', {
        vehicleId: d.vehicleId,
        name: d.name,
        type: km && days ? 'HYBRID' : km ? 'KM' : 'DATE',
        intervalKm: km,
        intervalDays: days,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['maintenance-plans'] });
      toast.success('Plan préventif créé');
      onOpenChange(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la création du plan')),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nouveau plan préventif</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div>
            <Label htmlFor="plan-vehicle">Véhicule *</Label>
            <Select value={watch('vehicleId')} onValueChange={(v) => setValue('vehicleId', v, { shouldValidate: true })}>
              <SelectTrigger id="plan-vehicle"><SelectValue placeholder="Choisir un véhicule" /></SelectTrigger>
              <SelectContent>
                {vehicles?.data?.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.plate} — {v.odometerKm.toLocaleString('fr-FR')} km
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.vehicleId && <p className="text-xs text-danger mt-1">{errors.vehicleId.message}</p>}
          </div>
          <div>
            <Label htmlFor="plan-name">Intervention *</Label>
            <Input id="plan-name" {...register('name')} placeholder="Vidange moteur + filtres" />
            {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan-km">Tous les … km</Label>
              <Input id="plan-km" inputMode="numeric" {...register('intervalKm')} placeholder="10000" />
              {errors.intervalKm && <p className="text-xs text-danger mt-1">{errors.intervalKm.message}</p>}
            </div>
            <div>
              <Label htmlFor="plan-months">Tous les … mois</Label>
              <Input id="plan-months" inputMode="numeric" {...register('intervalMonths')} placeholder="6" />
              {errors.intervalMonths && <p className="text-xs text-danger mt-1">{errors.intervalMonths.message}</p>}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Avec les deux, l&apos;échéance est le premier seuil atteint. À l&apos;échéance, un ordre de travail est
            créé automatiquement ; une fois terminé, la prochaine échéance est recalculée.
          </p>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Création…' : 'Créer le plan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
